// Map-overlay coordinate search (Google Maps style).
//
// One input that accepts decimal-degree coords in flexible formats:
//   66.31, 14.13            (comma-separated)
//   66.31 14.13             (space-separated)
//   66.31°N, 14.13°E        (degree markers + cardinals — stripped)
//
// On submit we hand (lat, lng) to SiteScreener.lookupParcelAtCoordinates,
// which flies the map, fetches the parcel from /api/v1/layers/parcel-at-point
// (with 2 km nearest-fallback), and opens Site Analysis on the result.
//
// Position (2026-05-27 redesign): bottom-centre, sitting just to the
// left of the LayerControls panel at bottom: 24px; right: 450px. Was
// previously top-left and collided with the AnalysisPanel dock once
// a site was selected. Now joins the map-controls cluster at the
// bottom of the map — central enough to be obvious, narrow enough
// (260 px) to leave breathing room.

import React, { useState } from 'react';

// Parse free-form decimal-degree input. Returns { lat, lng } or null.
// Tolerates degree markers, N/S/E/W, and either comma or whitespace
// separators. Caller validates ranges (we just say "valid number pair").
function parseCoordInput(raw) {
  if (!raw) return null;
  const cleaned = String(raw)
    .replace(/[°NSEW]/gi, '')
    .replace(/,/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  const parts = cleaned.split(' ').filter(Boolean);
  if (parts.length !== 2) return null;
  const lat = parseFloat(parts[0]);
  const lng = parseFloat(parts[1]);
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
  if (lat < -90 || lat > 90 || lng < -180 || lng > 180) return null;
  return { lat, lng };
}

export default function CoordinateSearchBox({ onLookup, variant = 'floating' }) {
  const [value, setValue] = useState('');
  // status shape: { kind: 'idle' | 'loading' | 'success' | 'error', message? }
  const [status, setStatus] = useState({ kind: 'idle' });

  const submit = async () => {
    const parsed = parseCoordInput(value);
    if (!parsed) {
      setStatus({ kind: 'error', message: 'Use decimal degrees: "66.31, 14.13"' });
      return;
    }
    if (!onLookup) return;
    setStatus({ kind: 'loading' });
    const result = await onLookup(parsed.lat, parsed.lng);
    if (result?.ok) {
      setStatus({ kind: 'success', message: result.info || 'Parcel selected' });
    } else {
      setStatus({ kind: 'error', message: result?.reason || 'Lookup failed' });
    }
  };

  const onKeyDown = (e) => {
    if (e.key === 'Enter') submit();
    else if (e.key === 'Escape') { setValue(''); setStatus({ kind: 'idle' }); }
  };

  // Two layouts: `inline` (rendered inside SiteFinder under the Find
  // button — no fixed positioning, full container width) vs the
  // legacy `floating` (bottom-centre map overlay, kept as a fallback
  // for any other caller that still mounts it on the map).
  const wrapperStyle = variant === 'inline' ? {
    width: '100%',
    padding: 0,
    background: 'transparent',
    border: 'none',
    boxShadow: 'none',
  } : {
    position: 'fixed',
    bottom: 24,
    right: 620,
    zIndex: 1001,
    width: 260,
    maxWidth: 'calc(100vw - 24px)',
    background: '#141720',
    border: '1px solid #252a3a',
    borderRadius: 8,
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.4)',
    padding: 6,
  };

  return (
    <div style={wrapperStyle}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
        <span
          aria-hidden
          style={{ color: '#7c829e', fontSize: 12, padding: '0 4px' }}
          title="Coordinate search"
        >⌖</span>
        <input
          type="text"
          inputMode="decimal"
          placeholder="Find by coords (66.31, 14.13)"
          value={value}
          onChange={(e) => { setValue(e.target.value); if (status.kind !== 'loading') setStatus({ kind: 'idle' }); }}
          onKeyDown={onKeyDown}
          disabled={status.kind === 'loading'}
          aria-label="Latitude, longitude"
          style={{
            flex: 1, minWidth: 0,
            padding: '5px 6px', fontSize: 12,
            background: '#020617', color: '#e2e8f0',
            border: '1px solid #1e293b', borderRadius: 4,
            outline: 'none',
          }}
        />
        <button
          type="button"
          onClick={submit}
          disabled={status.kind === 'loading' || !value.trim()}
          style={{
            padding: '5px 10px', fontSize: 11, fontWeight: 600,
            background: status.kind === 'loading' ? '#1e293b' : '#22c55e',
            color: status.kind === 'loading' ? '#94a3b8' : '#020617',
            border: 'none', borderRadius: 4,
            cursor: status.kind === 'loading' ? 'wait'
                  : !value.trim() ? 'not-allowed' : 'pointer',
            opacity: !value.trim() ? 0.55 : 1,
            whiteSpace: 'nowrap',
          }}
        >
          {status.kind === 'loading' ? '⟳' : 'Find'}
        </button>
      </div>

      {status.kind === 'loading' && (
        <div style={{ marginTop: 4, fontSize: 10, color: '#94a3b8' }}>
          Searching Matrikkelen…
        </div>
      )}
      {status.kind === 'error' && (
        <div style={{ marginTop: 4, fontSize: 10, color: '#f87171' }}>
          {status.message}
        </div>
      )}
      {status.kind === 'success' && (
        <div style={{ marginTop: 4, fontSize: 10, color: '#4ade80' }}>
          ✓ {status.message}
        </div>
      )}
    </div>
  );
}
